import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { usePageBootstrap } from "../../hooks/useBootstrapList";
import { useBootstrapWS } from "../../hooks/useBootstrapWS";
import { useAuth, hasPermission } from "../../context/AuthContext";
import { PageHeader, KpiTile, SectionCard, Pill } from "../../components/Ui";
import GatePassCreateForm from "../../components/GatePassCreateForm";
import GatePassDetailDialog from "../../components/GatePassDetailDialog";
import { Truck, UserRound, LogOut, ArrowRight } from "lucide-react";

export default function GuardDashboard() {
  const { user } = useAuth();
  const { data, reload } = usePageBootstrap({ path: "/dashboard/bootstrap", wsTypes: ["gate_pass"] });
  useBootstrapWS(["gate_pass"], reload);
  const [mode, setMode] = useState(null);
  const [detailPassId, setDetailPassId] = useState(null);
  const canCreate = hasPermission(user, "gate.pass.create");
  const canExit = hasPermission(user, "gate.pass.exit");

  const passes = data?.recent_in_yard || [];
  const summary = data?.summary || {};

  const inYard = passes.filter((p) => p.status === "IN");
  const vehiclesInYard = summary.vehicles_in_yard ?? inYard.filter((p) => p.purpose !== "VISITOR").length;
  const visitorsInYard = summary.visitors_in_yard ?? inYard.filter((p) => p.purpose === "VISITOR").length;
  const todayEntries = summary.today_entries ?? 0;

  const recentInYard = useMemo(
    () => [...inYard].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 8),
    [inYard],
  );

  const closeForm = () => setMode(null);

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="guard-dashboard">
      <PageHeader
        eyebrow="Gate Security"
        title="Gate Desk"
        description="Register vehicle inward and visitor entries. Check out vehicles when they leave the mill."
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <KpiTile label="In yard" value={summary.in_yard ?? vehiclesInYard + visitorsInYard} sub="Vehicles + visitors" accent testId="guard-kpi-yard" />
        <KpiTile label="Vehicles" value={vehiclesInYard} sub="Currently inside" testId="guard-kpi-vehicles" />
        <KpiTile label="Visitors" value={visitorsInYard} sub="On premises" testId="guard-kpi-visitors" />
        <KpiTile label="Today" value={todayEntries} sub="Entries logged" testId="guard-kpi-today" />
      </div>

      {canCreate && !mode && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <button
            type="button"
            onClick={() => setMode("vehicle")}
            className="flex items-center gap-4 rounded-2xl border-2 border-bansal-sage/30 bg-card p-5 text-left shadow-sm active:scale-[0.98] transition-transform touch-manipulation min-h-[88px]"
            data-testid="guard-action-vehicle-in"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-bansal-sage/15 text-primary">
              <Truck className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-foreground">Procurement inward</div>
              <div className="text-xs text-muted-foreground mt-0.5">Paddy truck entry</div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setMode("store")}
            className="flex items-center gap-4 rounded-2xl border-2 border-border bg-card p-5 text-left shadow-sm active:scale-[0.98] transition-transform touch-manipulation min-h-[88px]"
            data-testid="guard-action-store-in"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Truck className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-foreground">Store inward</div>
              <div className="text-xs text-muted-foreground mt-0.5">Packing, spares, assets</div>
            </div>
          </button>
          <Link
            to="/gate/outward"
            className="flex items-center gap-4 rounded-2xl border-2 border-sky-200 bg-card p-5 text-left shadow-sm active:scale-[0.98] transition-transform touch-manipulation min-h-[88px]"
            data-testid="guard-action-outward"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sky-100 text-sky-800">
              <LogOut className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-foreground">Outward supply</div>
              <div className="text-xs text-muted-foreground mt-0.5">Rice dispatch vehicles</div>
            </div>
          </Link>
          <button
            type="button"
            onClick={() => setMode("visitor")}
            className="flex items-center gap-4 rounded-2xl border-2 border-bansal-gold/40 bg-card p-5 text-left shadow-sm active:scale-[0.98] transition-transform touch-manipulation min-h-[88px]"
            data-testid="guard-action-visitor-in"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-bansal-gold/15 text-bansal-goldDark">
              <UserRound className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-foreground">Visitor in</div>
              <div className="text-xs text-muted-foreground mt-0.5">Walk-in or escorted visit</div>
            </div>
          </button>
        </div>
      )}

      {mode === "vehicle" && canCreate && (
        <SectionCard title="Procurement inward entry" testId="guard-vehicle-form">
          <GatePassCreateForm
            lockedPurpose="INWARD_PADDY"
            onSuccess={() => { reload(); closeForm(); }}
            onCancel={closeForm}
            compact
            testIdPrefix="guard-vehicle"
          />
        </SectionCard>
      )}

      {mode === "store" && canCreate && (
        <SectionCard title="Store inward entry" testId="guard-store-form">
          <GatePassCreateForm
            lockedPurpose="INWARD_STORE"
            onSuccess={() => { reload(); closeForm(); }}
            onCancel={closeForm}
            compact
            testIdPrefix="guard-store"
          />
        </SectionCard>
      )}

      {mode === "visitor" && canCreate && (
        <SectionCard title="Visitor entry" testId="guard-visitor-form">
          <GatePassCreateForm
            lockedPurpose="VISITOR"
            onSuccess={() => { reload(); closeForm(); }}
            onCancel={closeForm}
            compact
            testIdPrefix="guard-visitor"
          />
        </SectionCard>
      )}

      <SectionCard
        title="Currently in yard"
        testId="guard-in-yard"
        action={
          <Link to="/gate/procurement" className="text-xs link-brand inline-flex items-center gap-1">
            Full register <ArrowRight className="h-3 w-3" />
          </Link>
        }
      >
        {recentInYard.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No vehicles or visitors in the yard.</p>
        ) : (
          <div className="space-y-2">
            {recentInYard.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setDetailPassId(p.id)}
                className="w-full flex items-center gap-3 rounded-xl border border-border bg-muted/40 px-3 py-3 text-left active:bg-muted transition-colors touch-manipulation"
                data-testid={`guard-yard-${p.code}`}
              >
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${p.purpose === "VISITOR" ? "bg-bansal-gold/15 text-bansal-goldDark" : "bg-bansal-sage/15 text-primary"}`}>
                  {p.purpose === "VISITOR" ? <UserRound className="h-5 w-5" /> : <Truck className="h-5 w-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-medium">{p.code}</span>
                    <Pill tone={p.purpose === "VISITOR" ? "gold" : "sage"}>{p.purpose.replaceAll("_", " ")}</Pill>
                  </div>
                  <div className="text-sm truncate mt-0.5">
                    {p.purpose === "VISITOR" ? p.driver_name : `${p.vehicle_number} · ${p.driver_name}`}
                  </div>
                  {p.party_name && <div className="text-xs text-muted-foreground truncate">{p.party_name}</div>}
                </div>
                {canExit && (
                  <LogOut className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                )}
              </button>
            ))}
          </div>
        )}
      </SectionCard>

      <GatePassDetailDialog
        passId={detailPassId}
        open={!!detailPassId}
        onOpenChange={(v) => { if (!v) setDetailPassId(null); }}
        onUpdated={reload}
      />
    </div>
  );
}
